/**
 * User Preferences API Service
 * Complete API methods for user preferences CRUD operations
 */

import { apiClient } from '@/app/utils/apiClient';
import { UserPreferences } from '@/app/types/auth';
import { logger } from '@/app/utils/logger';
import { PARTITION_KEY } from '@/app/constants/coreConstants';

/**
 * Backend API Response Structure (matches IResult<T> from backend)
 */
export interface ApiResponse<T> {
  succeeded: boolean;
  statusCode: number;
  message?: string;
  messages?: string[];
  data?: T;
}

/**
 * User Preference Payload (matches backend UserPreference_OutputPayload)
 */
export interface UserPreferencePayload {
  userId: number;
  languageCode: string;
  themePreference?: string;
  colorThemePreference?: string;
  timezone?: string;
  dateFormatPreference?: string;
  dateTimeFormatPattern?: string | null;
  emailNotificationsEnabled?: boolean;
  pushNotificationsEnabled?: boolean;
  notificationSoundEnabled?: boolean;
  notificationSoundVolume?: number;
  notificationToastEnabled?: boolean;
  notificationToastDuration?: number;
  notificationToastPosition?: string;
  notificationSystemEnabled?: boolean;
  notificationToolEnabled?: boolean;
  notificationAdminEnabled?: boolean;
  notificationSecurityEnabled?: boolean;
  notificationDndEnabled?: boolean;
  notificationDndStartTime?: string;
  notificationDndEndTime?: string;
  activeTenantId?: number | null;
  activeBuyerPartyId?: number | null;
  activeSupplierPartyId?: number | null;
  extendedSettings?: string;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

/**
 * Get All Preferences Response (matches backend UserPreference_GetAll_Response)
 */
export interface GetAllPreferencesResponse {
  preference: UserPreferencePayload;
}

/**
 * Update All Preferences Request (matches backend UserPreference_UpdateAll_Request)
 */
export interface UpdateAllPreferencesRequest {
  PartitionKey: string;
  UserId: number;
  LanguageCode?: string;
  ThemePreference?: string;
  ColorThemePreference?: string;
  Timezone?: string;
  DateFormatPreference?: string;
  DateTimeFormatPattern?: string | null;
  EmailNotificationsEnabled?: boolean;
  PushNotificationsEnabled?: boolean;
  NotificationSoundEnabled?: boolean;
  NotificationSoundVolume?: number;
  NotificationToastEnabled?: boolean;
  NotificationToastDuration?: number;
  NotificationToastPosition?: string;
  NotificationSystemEnabled?: boolean;
  NotificationToolEnabled?: boolean;
  NotificationAdminEnabled?: boolean;
  NotificationSecurityEnabled?: boolean;
  NotificationDndEnabled?: boolean;
  NotificationDndStartTime?: string;
  NotificationDndEndTime?: string;
  ActiveTenantId?: number | null;
  ActiveBuyerPartyId?: number | null;
  ActiveSupplierPartyId?: number | null;
  /**
   * Opt-in flag for writing the (ActiveTenantId, ActiveBuyerPartyId,
   * ActiveSupplierPartyId) triple. Set automatically by `mapPreferencesToRequest`
   * whenever any of those three keys is present in the partial update. When
   * false / omitted, the backend leaves all three columns untouched — required
   * so that unrelated partial updates (theme, language, notifications) don't
   * clobber the active context with default-null values.
   */
  UpdateActiveContext?: boolean;
}

/**
 * Update All Preferences Response (matches backend UserPreference_UpdateAll_Response)
 */
export interface UpdateAllPreferencesResponse {
  preference: UserPreferencePayload;
}

/**
 * User Preferences API Service
 */
export const userPreferencesApi = {
  /**
   * Get user preferences from backend
   */
  async getUserPreferences(userId: number): Promise<UserPreferences> {
    try {
      logger.apiCall('GET', '/api/v1/core/settings/preferences/all', { userId });

      const response = await apiClient.get<ApiResponse<GetAllPreferencesResponse>>(
        `/api/v1/core/settings/preferences/all`,
        { params: { userId } }
      );

      logger.apiResponse('GET', '/api/v1/core/settings/preferences/all', response.status);

      if (!response.data.succeeded) {
        const errorMessages = response.data.messages?.join(', ') || response.data.message || 'Failed to fetch user preferences';
        throw new Error(errorMessages);
      }

      if (!response.data.data?.preference) {
        throw new Error('No preference data returned from server');
      }

      return mapPayloadToPreferences(response.data.data.preference);
    } catch (error) {
      logger.error('Failed to fetch user preferences', { error, userId });
      throw error;
    }
  },

  /**
   * Update user preferences (partial update)
   */
  async updateUserPreferences(
    userId: number,
    preferences: Partial<UserPreferences>
  ): Promise<UserPreferences> {
    try {
      const request: UpdateAllPreferencesRequest = {
        PartitionKey: PARTITION_KEY,
        UserId: userId,
        ...mapPreferencesToRequest(preferences)
      };

      logger.apiCall('PUT', '/api/v1/core/settings/preferences/all', { userId });

      const response = await apiClient.put<ApiResponse<UpdateAllPreferencesResponse>>(
        `/api/v1/core/settings/preferences/all`,
        request
      );

      logger.apiResponse('PUT', '/api/v1/core/settings/preferences/all', response.status);

      if (!response.data.succeeded) {
        const errorMessages = response.data.messages?.join(', ') || response.data.message || 'Failed to update user preferences';
        throw new Error(errorMessages);
      }

      if (!response.data.data?.preference) {
        throw new Error('No preference data returned from server');
      }

      return mapPayloadToPreferences(response.data.data.preference);
    } catch (error) {
      logger.error('Failed to update user preferences', { error, userId });
      throw error;
    }
  },

  /**
   * Get default preferences from system configuration
   * Falls back to hardcoded defaults if configuration is not available
   */
  async getDefaultPreferences(): Promise<Partial<UserPreferences>> {
    try {
      const response = await apiClient.get<ApiResponse<Array<{
        configKey: string;
        configValue: string;
      }>>>(`/api/v1/core/settings/configuration`, {
        params: {
          scopePath: '/global',
          category: 'UserPreferenceDefaults'
        }
      });

      if (response.data.succeeded && response.data.data) {
        return mapConfigurationToDefaults(response.data.data);
      }
    } catch {
      logger.warn('Could not fetch default preferences from configuration, using hardcoded defaults');
    }

    // Fallback to hardcoded defaults
    return getHardcodedDefaults();
  },

  /**
   * Batch update multiple preferences
   */
  async batchUpdatePreferences(
    userId: number,
    updates: Array<{ key: keyof UserPreferences; value: unknown }>
  ): Promise<UserPreferences> {
    const preferences: Partial<UserPreferences> = {};
    
    updates.forEach(({ key, value }) => {
      (preferences as Record<string, unknown>)[key] = value;
    });

    return this.updateUserPreferences(userId, preferences);
  },

  /**
   * Reset user preferences to system defaults
   */
  async resetUserPreferences(userId: number): Promise<UserPreferences> {
    try {
      const defaults = await this.getDefaultPreferences();
      return this.updateUserPreferences(userId, defaults);
    } catch (err) {
      logger.error('Failed to reset user preferences', { error: err, userId });
      throw err;
    }
  }
};

/**
 * Map backend payload to frontend UserPreferences interface
 */
function mapPayloadToPreferences(payload: UserPreferencePayload): UserPreferences {
  return {
    userId: payload.userId,
    language: payload.languageCode,
    theme: (payload.themePreference as 'light' | 'dark' | 'system') || 'system',
    colorTheme: (payload.colorThemePreference as 'yellow' | 'green' | 'blue' | 'purple') || 'purple',
    timezone: payload.timezone || 'UTC',
    dateFormat: (payload.dateFormatPreference as 'relative' | 'absolute') || 'relative',
    dateTimeFormatPattern: payload.dateTimeFormatPattern ?? null,
    emailNotifications: payload.emailNotificationsEnabled ?? true,
    pushNotifications: payload.pushNotificationsEnabled ?? true,
    notificationSoundEnabled: payload.notificationSoundEnabled ?? true,
    notificationSoundVolume: payload.notificationSoundVolume ?? 80,
    notificationToastEnabled: payload.notificationToastEnabled ?? true,
    notificationToastDuration: payload.notificationToastDuration ?? 5,
    notificationToastPosition: (payload.notificationToastPosition as 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left') || 'top-right',
    notificationSystemEnabled: payload.notificationSystemEnabled ?? true,
    notificationToolEnabled: payload.notificationToolEnabled ?? true,
    notificationAdminEnabled: payload.notificationAdminEnabled ?? true,
    notificationSecurityEnabled: payload.notificationSecurityEnabled ?? true,
    notificationDndEnabled: payload.notificationDndEnabled ?? false,
    notificationDndStartTime: payload.notificationDndStartTime,
    notificationDndEndTime: payload.notificationDndEndTime,
    activeTenantId: payload.activeTenantId,
    activeBuyerPartyId: payload.activeBuyerPartyId,
    activeSupplierPartyId: payload.activeSupplierPartyId,
    extendedSettings: payload.extendedSettings
      ? JSON.parse(payload.extendedSettings) 
      : undefined,
    createdAt: payload.createdAt,
    createdBy: payload.createdBy,
    updatedAt: payload.updatedAt,
    updatedBy: payload.updatedBy,
  };
}

/**
 * Map frontend UserPreferences to backend request
 */
function mapPreferencesToRequest(
  preferences: Partial<UserPreferences>
): Partial<UpdateAllPreferencesRequest> {
  const request: Partial<UpdateAllPreferencesRequest> = {};

  if (preferences.language !== undefined) {
    request.LanguageCode = preferences.language;
  }
  if (preferences.theme !== undefined) {
    request.ThemePreference = preferences.theme;
  }
  if (preferences.colorTheme !== undefined) {
    request.ColorThemePreference = preferences.colorTheme;
  }
  if (preferences.timezone !== undefined) {
    request.Timezone = preferences.timezone;
  }
  if (preferences.dateFormat !== undefined) {
    request.DateFormatPreference = preferences.dateFormat;
  }
  if ('dateTimeFormatPattern' in preferences) {
    request.DateTimeFormatPattern = preferences.dateTimeFormatPattern ?? null;
  }
  if (preferences.emailNotifications !== undefined) {
    request.EmailNotificationsEnabled = preferences.emailNotifications;
  }
  if (preferences.pushNotifications !== undefined) {
    request.PushNotificationsEnabled = preferences.pushNotifications;
  }
  
  // Notification preferences
  if (preferences.notificationSoundEnabled !== undefined) {
    request.NotificationSoundEnabled = preferences.notificationSoundEnabled;
  }
  if (preferences.notificationSoundVolume !== undefined) {
    request.NotificationSoundVolume = preferences.notificationSoundVolume;
  }
  if (preferences.notificationToastEnabled !== undefined) {
    request.NotificationToastEnabled = preferences.notificationToastEnabled;
  }
  if (preferences.notificationToastDuration !== undefined) {
    request.NotificationToastDuration = preferences.notificationToastDuration;
  }
  if (preferences.notificationToastPosition !== undefined) {
    request.NotificationToastPosition = preferences.notificationToastPosition;
  }
  if (preferences.notificationSystemEnabled !== undefined) {
    request.NotificationSystemEnabled = preferences.notificationSystemEnabled;
  }
  if (preferences.notificationToolEnabled !== undefined) {
    request.NotificationToolEnabled = preferences.notificationToolEnabled;
  }
  if (preferences.notificationAdminEnabled !== undefined) {
    request.NotificationAdminEnabled = preferences.notificationAdminEnabled;
  }
  if (preferences.notificationSecurityEnabled !== undefined) {
    request.NotificationSecurityEnabled = preferences.notificationSecurityEnabled;
  }
  if (preferences.notificationDndEnabled !== undefined) {
    request.NotificationDndEnabled = preferences.notificationDndEnabled;
  }
  if (preferences.notificationDndStartTime !== undefined) {
    request.NotificationDndStartTime = preferences.notificationDndStartTime;
  }
  if (preferences.notificationDndEndTime !== undefined) {
    request.NotificationDndEndTime = preferences.notificationDndEndTime;
  }
  // Active working-context triple — 'in' check so explicit null clears the
  // value, and presence of any of the three flips UpdateActiveContext so the
  // backend writes the group. Without the flag the backend would not be able
  // to tell "absent in JSON" from "null" (nullable C# props default to null)
  // and would clobber the triple on every unrelated partial update.
  let touchesActiveContext = false;
  if ('activeTenantId' in preferences) {
    request.ActiveTenantId = preferences.activeTenantId ?? null;
    touchesActiveContext = true;
  }
  if ('activeBuyerPartyId' in preferences) {
    request.ActiveBuyerPartyId = preferences.activeBuyerPartyId ?? null;
    touchesActiveContext = true;
  }
  if ('activeSupplierPartyId' in preferences) {
    request.ActiveSupplierPartyId = preferences.activeSupplierPartyId ?? null;
    touchesActiveContext = true;
  }
  if (touchesActiveContext) {
    request.UpdateActiveContext = true;
  }

  return request;
}

/**
 * Map system configuration to default preferences
 */
function mapConfigurationToDefaults(
  configurations: Array<{ configKey: string; configValue: string }>
): Partial<UserPreferences> {
  const defaults: Partial<UserPreferences> = {};

  configurations.forEach(({ configKey, configValue }) => {
    switch (configKey) {
      case 'user.default_theme':
        defaults.theme = configValue as 'light' | 'dark' | 'system';
        break;
      case 'user.default_color_theme':
        defaults.colorTheme = configValue as 'yellow' | 'green' | 'blue' | 'purple';
        break;
      case 'user.default_timezone':
        defaults.timezone = configValue;
        break;
      case 'user.default_date_format':
        defaults.dateFormat = configValue as 'relative' | 'absolute';
        break;
      case 'user.default_email_notifications':
        defaults.emailNotifications = configValue.toLowerCase() === 'true';
        break;
      case 'user.default_push_notifications':
        defaults.pushNotifications = configValue.toLowerCase() === 'true';
        break;
    }
  });

  return defaults;
}

/**
 * Get hardcoded default preferences
 */
function getHardcodedDefaults(): Partial<UserPreferences> {
  return {
    language: 'en-US',
    theme: 'system',
    colorTheme: 'purple',
    timezone: 'UTC',
    dateFormat: 'relative',
    emailNotifications: true,
    pushNotifications: true,
  };
}
